import React, { useContext } from 'react';
import { useRouter } from 'next/router';

import getResizedImageUrl from '@components/havenly/lib/getResizedImageUrl';
import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import * as clickTypes from '@constants/tracking/clickTypes';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import UserContext from '@context/User/user-context';
import formatPrice from '@utils/shared/format/formatPrice';
import { v4 as uuidv4 } from 'uuid';
import useAddToCart from '@hooks/User/useAddToCart';
import styles from './ContentAltStyles.module.scss';
import ShoppingCart from './assets/shopping-cart.svg';
import useWindowSize from '../../../../hooks/useWindowSize/useWindowSize';
import { breakpoints } from '../../../../hooks/useMediaQuery';

export default function ContentAlt(props) {
  const {
    assetId, annotationModel, annotationModelId, vendorVariant
  } = props;

  const { open: openLoginRegisterModal } = useContext(
    LoginRegisterModalContext
  );
  const { user } = useContext(UserContext);
  const router = useRouter();

  const { width: windowWidth } = useWindowSize();
  const isMobile = () => !!windowWidth && windowWidth < breakpoints.desktop;

  const imageUrls = {
    '1x': getResizedImageUrl({
      url: vendorVariant.image.uri,
      width: 45,
      height: 45,
      quality: 100,
    }),
    '2x': getResizedImageUrl({
      url: vendorVariant.image.uri,
      width: 90,
      height: 90,
      quality: 50,
    }),
  };

  const [addToCart] = useAddToCart({
    location: 'shop_landing_featured_board_product_bubble',
    vendorVariant
  });

  const handleAddToCart = async () => {
    const listId = uuidv4();

    const doAddToCart = async () => {
      await addToCart(vendorVariant.id, { listId });
    };

    if (!user) {
      openLoginRegisterModal({
        onLoginSuccess: doAddToCart,
        onRegisterSuccess: doAddToCart,
        source: 'shop_landing_featured_board_product_bubble',
        magicLinkRedirectUrl: router.asPath,
      });
    } else {
      await doAddToCart();
    }
  };

  return (
    <div className={styles.Content}>
      {
        !isMobile() &&
        (
          <a
            className={styles.Image}
            href={`/products/details/${annotationModelId}`}
            onClick={(e) => {
              e.stopPropagation();
              handleTrackAnnotationContentClicked(
                assetId,
                annotationModel,
                annotationModelId,
                clickTypes.ANNOTATION_LINK_IMG
              );
            }}
            rel="noopener nofollow noreferrer"
            target="_blank"
          >
            <img alt="" src={imageUrls['1x']} srcSet={`${imageUrls['2x']} 2x`} />
          </a>
        )
      }
      <div>
        <a
          className={styles.Title}
          href={`/products/details/${annotationModelId}`}
          onClick={(e) => {
            e.stopPropagation();
            handleTrackAnnotationContentClicked(
              assetId,
              annotationModel,
              annotationModelId,
              clickTypes.ANNOTATION_LINK_TITLE
            );
          }}
          rel="noopener nofollow noreferrer"
          target="_blank"
        >
          {vendorVariant.title}
        </a>
        <div className={styles.Text}>
          {formatPrice(vendorVariant.price, true)}
        </div>
      </div>
      {
        !isMobile() && (
          <div
            onClick={handleAddToCart}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleAddToCart();
            }}
            className={styles.AddToCart}
          >
            <ShoppingCart />
          </div>
        )
      }
    </div>
  );
}

function handleTrackAnnotationContentClicked(
  assetId,
  annotationModelName,
  annotationModelId,
  clickType
) {
  analyticsTrack('explore_image_annotation_clicked', {
    assetId,
    annotationModelName,
    annotationModelId,
    clickType,
  });
}
